import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LostProductDetailsModal from "../../Components/LostProductDetailsModal";
import lostProductService from "../../Services/PharmacylostProductService";
import moment from "moment";
import {
  MdAdd,
  MdInventory2,
  MdReceiptLong,
  MdSearch,
  MdDateRange,
  MdTrendingDown,
  MdVisibility,
} from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useNavigate } from "react-router-dom";

const ITEMS_PER_PAGE = 10;

const LostProducts = () => {
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLostProduct, setSelectedLostProduct] = useState(null);

  useEffect(() => {
    const getLostProducts = async () => {
      try {
        setLoading(true);
        const response = await lostProductService.fetchAll();
        setRecords(response.lostProducts);
      } catch (error) {
        toast.error("Error fetching Lost Products");
      } finally {
        setLoading(false);
      }
    };
    getLostProducts();
  }, []);

  useEffect(() => {
    const result =
      records?.filter((item) =>
        item?.customer_name?.toLowerCase().includes(searchData.toLowerCase())
      ) || [];
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchData, records]);

  useEffect(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    setPaginatedData(filteredData?.slice(start, start + ITEMS_PER_PAGE) || []);
  }, [currentPage, filteredData]);

  const handleViewInvoice = async (lostProductId) => {
    try {
      setInvoiceLoading(lostProductId);
      const response = await lostProductService.fetchSingle(lostProductId);
      setSelectedLostProduct(response.lostProduct);
      setIsOpen(true);
    } catch (error) {
      toast.error("Error fetching lost product details");
    } finally {
      setInvoiceLoading(null);
    }
  };

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const totalAmount = records.reduce(
    (sum, s) => sum + parseFloat(s.amount || 0),
    0
  );

  const currentMonth = moment();
  const currentMonthLostAmount = records
    .filter((s) => moment(s.date).isSame(currentMonth, "month"))
    .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0);

  const formatCurrency = (amount) =>
    `Rs. ${amount.toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.5s ease both; }
        * { font-family: 'DM Sans', sans-serif; }
        .serif { font-family: 'DM Serif Display', serif; }
      `}</style>

      <div className="min-h-screen bg-[#f4f7fb] py-8 px-4 fade-up">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #1F2937, #111827)" }}
                >
                  <MdReceiptLong size={18} className="text-white" />
                </div>
                <h1 className="text-[#0a2540] text-[26px] serif">Pharmacy Lost Products</h1>
              </div>
              <p className="text-[#8a9bb0] text-sm ml-12">
                {filteredData.length} record{filteredData.length !== 1 ? "s" : ""} found
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/admin/pharmacy/LostProducts/addlostproduct")}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1F2937] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#111827] hover:shadow-md focus:outline-none focus:ring-4 focus:ring-[#1F2937]/20"
            >
              <MdAdd size={18} />
              Add Lost Product
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 mb-6">
            <KpiCard
              label="Total Loss Amount"
              value={formatCurrency(totalAmount)}
              icon={MdTrendingDown}
              iconClass="bg-[#fff1f2] text-[#e11d48]"
              valueClass="text-[#e11d48]"
            />
            <KpiCard
              label="Lost Product Records"
              value={records.length}
              icon={MdInventory2}
              iconClass="bg-[#f4f7fb] text-[#1F2937]"
              valueClass="text-[#0a2540]"
            />
            <KpiCard
              label="This Month's Loss Amount"
              value={formatCurrency(currentMonthLostAmount)}
              icon={MdDateRange}
              iconClass="bg-[#eff6ff] text-[#2563eb]"
              valueClass="text-[#0a2540]"
              helper={currentMonth.format("MMMM YYYY")}
            />
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#eef1f6]">
              <div className="relative w-full sm:w-[360px]">
                <MdSearch
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a9bb0] pointer-events-none"
                />
                <input
                  type="search"
                  placeholder="Search by Month name..."
                  value={searchData}
                  onChange={(e) => setSearchData(e.target.value)}
                  className="w-full pl-10 pr-4 py-[10px] bg-[#f8fafc] border-[1.5px] border-[#dde4ed] rounded-xl text-[14px] text-[#0a2540] outline-none transition-all placeholder:text-[#b0bcc9] focus:border-[#1F2937] focus:shadow-[0_0_0_4px_rgba(31,41,55,0.1)] focus:bg-white"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                 <tr style={{ background: '#1F2937' }}>
                    {["Sr.no.", "Month Name", "Amount", "Date", "Action"].map((h) => (
                      <th
                        key={h}
                        className={`px-5 py-3.5 text-[11px] font-semibold uppercase tracking-[0.6px] text-white whitespace-nowrap ${h === "Action" ? "text-center" : ""}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center py-16">
                        <div className="flex flex-col items-center gap-3 text-[#8a9bb0]">
                          <AiOutlineLoading3Quarters
                            size={28}
                            className="animate-spin text-[#1F2937]"
                          />
                          <span className="text-sm">Loading lost products…</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((lostProduct, index) => (
                      <tr
                        key={lostProduct?.id}
                        className={`text-[13px] border-b border-[#f0f4f8] transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-[#fafbfd]"
                        } hover:bg-[#f4f7fb]`}
                      >
                        <td className="px-5 py-3.5 font-bold text-[#1F2937]">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td className="px-5 py-3.5 font-semibold text-[#0a2540]">
                          {lostProduct?.customer_name}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-semibold text-[#0a2540]">
                            Rs.{" "}
                            {parseFloat(lostProduct?.amount).toLocaleString("en-PK", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-[#4a6080]">
                          <span className="inline-flex items-center gap-1.5 text-[12px] bg-[#f4f7fb] border border-[#eef1f6] px-2.5 py-1 rounded-lg">
                            {moment(lostProduct.date).format("ll")}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => handleViewInvoice(lostProduct.id)}
                            disabled={invoiceLoading === lostProduct.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-[#dde4ed] text-[#4a6080] hover:border-[#1F2937] hover:text-[#1F2937] hover:bg-[#f4f7fb] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {invoiceLoading === lostProduct.id ? (
                              <AiOutlineLoading3Quarters
                                size={13}
                                className="animate-spin"
                              />
                            ) : (
                              <MdVisibility size={14} />
                            )}
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-14">
                        <div className="flex flex-col items-center gap-2 text-[#b0bcc9]">
                          <MdReceiptLong size={32} className="text-[#dde4ed]" />
                          <span className="text-sm">
                            {searchData
                              ? "No lost products match your search."
                              : "No lost products records found."}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-[#eef1f6] flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-[12px] text-[#8a9bb0]">
                  Showing{" "}
                  {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)}{" "}
                  of {filteredData.length} results
                </p>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#dde4ed] text-[#4a6080] text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-[#1F2937] hover:enabled:text-[#1F2937] transition-colors"
                  >
                    ‹
                  </button>

                  {pageNumbers()[0] > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#dde4ed] text-[13px] text-[#4a6080] hover:border-[#1F2937] hover:text-[#1F2937] transition-colors"
                      >
                        1
                      </button>
                      {pageNumbers()[0] > 2 && (
                        <span className="text-[#b0bcc9] text-sm px-1">…</span>
                      )}
                    </>
                  )}

                  {pageNumbers().map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg border text-[13px] font-medium transition-colors ${
                        page === currentPage
                          ? "border-[#1F2937] bg-[#1F2937] text-white"
                          : "border-[#dde4ed] text-[#4a6080] hover:border-[#1F2937] hover:text-[#1F2937]"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {pageNumbers()[pageNumbers().length - 1] < totalPages && (
                    <>
                      {pageNumbers()[pageNumbers().length - 1] < totalPages - 1 && (
                        <span className="text-[#b0bcc9] text-sm px-1">…</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#dde4ed] text-[13px] text-[#4a6080] hover:border-[#1F2937] hover:text-[#1F2937] transition-colors"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#dde4ed] text-[#4a6080] text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:enabled:border-[#1F2937] hover:enabled:text-[#1F2937] transition-colors"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <LostProductDetailsModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        lostProduct={selectedLostProduct}
      />
    </>
  );
};

const KpiCard = ({ label, value, icon: Icon, iconClass, valueClass, helper }) => (
  <div className="rounded-2xl border border-[#eef1f6] bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0]">
          {label}
        </p>
        <p className={`mt-2 text-[22px] font-bold ${valueClass}`}>{value}</p>
        {helper && <p className="mt-1 text-xs text-[#8a9bb0]">{helper}</p>}
      </div>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}>
        <Icon size={20} />
      </div>
    </div>
  </div>
);

export default LostProducts;
